/**
 * Grill King — full-screen PDP gallery viewer (snippets/egj-gallery-viewer.liquid).
 *  - opens from any gallery image (replaces the legacy product-media-modal) and
 *    from the "+N" thumbnail tile, which lands on the "All photos" grid
 *  - swipe / arrows / keyboard; double-click, pinch, ctrl+wheel or the zoom
 *    button to zoom, drag to pan; swipe down to close on touch
 *  - closing moves the page gallery to the last viewed slide
 */
(() => {
  if (window.__egjGalleryViewer) return;
  window.__egjGalleryViewer = true;

  const MAX_SCALE = 4;
  const TAP_ZOOM = 2.5;
  const SWIPE_RATIO = 0.18;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

  class Viewer {
    constructor(dialog) {
      this.dialog = dialog;
      this.sectionId = dialog.dataset.egjGv;
      this.gallery = document.getElementById(`MediaGallery-${this.sectionId}`);
      const data = JSON.parse(document.getElementById(`EgjGalleryData-${this.sectionId}`).textContent);
      this.data = data.items || {};
      this.track = dialog.querySelector('[data-gv-track]');
      this.stage = dialog.querySelector('[data-gv-stage]');
      this.thumbs = dialog.querySelector('[data-gv-thumbs]');
      this.counter = dialog.querySelector('[data-gv-counter]');
      this.gridView = dialog.querySelector('[data-gv-gridview]');
      this.gridInner = dialog.querySelector('[data-gv-grid-inner]');
      this.gridBtn = dialog.querySelector('[data-gv-grid]');
      this.gridLabel = dialog.querySelector('[data-gv-grid-label]');
      this.zoomBtn = dialog.querySelector('[data-gv-zoom]');
      this.index = 0;
      this.zoom = { scale: 1, x: 0, y: 0 };
      this.pointers = new Map();
      this.built = false;
      this.bind();
    }

    /* Slide order mirrors the rendered gallery, so variant images the theme
       hides stay out of the viewer too. */
    keys() {
      const slides = this.gallery
        ? [...this.gallery.querySelectorAll('.product__media-list > [data-media-id]')]
        : [];
      const visible = slides.filter((slide) => !slide.classList.contains('product__media-item--variant') || slide.classList.contains('is-active'));
      const keys = (visible.length ? visible : slides).map((slide) => slide.dataset.mediaId).filter((key) => this.data[key]);
      return keys.length ? keys : Object.keys(this.data);
    }

    build() {
      this.order = this.keys();
      this.track.innerHTML = '';
      this.thumbs.innerHTML = '';
      this.gridInner.innerHTML = '';

      this.order.forEach((key, i) => {
        const item = this.data[key];
        const label = `${item.alt} (${i + 1} of ${this.order.length})`;

        const slide = document.createElement('div');
        slide.className = 'egj-gv__slide';
        slide.dataset.index = String(i);
        slide.setAttribute('role', 'group');
        slide.setAttribute('aria-roledescription', 'slide');
        slide.setAttribute('aria-label', label);
        const frame = document.createElement('div');
        frame.className = 'egj-gv__frame';
        frame.style.setProperty('--gv-ratio', `${item.w} / ${item.h}`);
        slide.appendChild(frame);
        this.track.appendChild(slide);

        const thumb = document.createElement('button');
        thumb.type = 'button';
        thumb.className = 'egj-gv__thumb';
        thumb.dataset.index = String(i);
        thumb.setAttribute('aria-label', `Show image ${i + 1}`);
        thumb.innerHTML = `<img src="${item.thumb}" alt="" loading="lazy" decoding="async" draggable="false">`;
        if (item.type !== 'image') thumb.classList.add('egj-gv__thumb--media');
        this.thumbs.appendChild(thumb);

        const tile = document.createElement('button');
        tile.type = 'button';
        tile.className = 'egj-gv__tile';
        tile.dataset.index = String(i);
        tile.setAttribute('aria-label', `Open ${label}`);
        tile.style.setProperty('--gv-ratio', `${item.w} / ${item.h}`);
        tile.innerHTML = `<img src="${item.tile}" alt="" loading="lazy" decoding="async" draggable="false">`;
        if (item.type !== 'image') tile.classList.add('egj-gv__tile--media');
        this.gridInner.appendChild(tile);
      });

      this.gridLabel.textContent = `All photos (${this.order.length})`;
      this.dialog.classList.toggle('egj-gv--single', this.order.length < 2);
      this.built = true;
    }

    frame(i) {
      return this.track.children[i] && this.track.children[i].firstElementChild;
    }

    /* Only the current slide and its neighbours carry real media. */
    hydrate(i) {
      const frame = this.frame(i);
      if (!frame || frame.dataset.ready) return;
      const item = this.data[this.order[i]];
      frame.dataset.ready = '1';

      if (item.type === 'external_video' && item.embed) {
        frame.dataset.embed = item.embed;
        frame.innerHTML = `<img class="egj-gv__img" src="${item.src}" alt="" draggable="false">`;
        return;
      }
      if (item.type === 'video' && item.video) {
        frame.innerHTML = `<video class="egj-gv__video" src="${item.video}" poster="${item.src}" controls playsinline preload="none"></video>`;
        return;
      }
      const img = document.createElement('img');
      img.className = 'egj-gv__img';
      img.alt = item.alt;
      img.decoding = 'async';
      img.draggable = false;
      img.sizes = '100vw';
      img.srcset = item.srcset;
      img.src = item.src;
      img.width = item.w;
      img.height = item.h;
      img.addEventListener('load', () => frame.classList.add('is-loaded'), { once: true });
      if (img.complete) frame.classList.add('is-loaded');
      frame.appendChild(img);
    }

    activateMedia(i, active) {
      const frame = this.frame(i);
      if (!frame) return;
      const video = frame.querySelector('video');
      if (video && !active) video.pause();
      if (frame.dataset.embed) {
        if (active && !frame.querySelector('iframe')) {
          frame.insertAdjacentHTML('beforeend', `<iframe class="egj-gv__embed" src="${frame.dataset.embed}" allow="autoplay; encrypted-media; fullscreen; picture-in-picture" allowfullscreen title="Product video"></iframe>`);
        } else if (!active) {
          const iframe = frame.querySelector('iframe');
          if (iframe) iframe.remove();
        }
      }
    }

    go(i, { instant = false } = {}) {
      const total = this.order.length;
      if (!total) return;
      const next = clamp(i, 0, total - 1);
      if (next !== this.index) {
        this.resetZoom(true);
        this.activateMedia(this.index, false);
      }
      this.index = next;
      [next - 1, next, next + 1, next + 2].forEach((n) => n >= 0 && n < total && this.hydrate(n));
      this.activateMedia(next, true);

      this.track.style.transition = instant || reduceMotion.matches ? 'none' : '';
      this.track.style.transform = `translate3d(${-next * 100}%, 0, 0)`;
      [...this.track.children].forEach((slide, n) => slide.setAttribute('aria-hidden', n === next ? 'false' : 'true'));

      this.counter.textContent = `${next + 1} / ${total}`;
      this.dialog.querySelector('[data-gv-prev]').disabled = next === 0;
      this.dialog.querySelector('[data-gv-next]').disabled = next === total - 1;

      [...this.thumbs.children].forEach((thumb, n) => {
        thumb.classList.toggle('is-active', n === next);
        thumb.setAttribute('aria-current', n === next ? 'true' : 'false');
      });
      this.centerThumb(instant);
      this.syncZoomButton();
    }

    centerThumb(instant) {
      const thumb = this.thumbs.children[this.index];
      if (!thumb) return;
      const left = thumb.offsetLeft - (this.thumbs.clientWidth - thumb.offsetWidth) / 2;
      this.thumbs.scrollTo({ left, behavior: instant || reduceMotion.matches ? 'auto' : 'smooth' });
    }

    /* ---------------------------------------------------------------- zoom */

    currentImg() {
      const frame = this.frame(this.index);
      return frame && frame.querySelector('img.egj-gv__img:not([data-poster])');
    }

    canZoom() {
      const item = this.data[this.order[this.index]];
      return item && item.type !== 'video' && item.type !== 'external_video' && !!this.currentImg();
    }

    applyZoom(animate) {
      const img = this.currentImg();
      if (!img) return;
      const { scale, x, y } = this.zoom;
      img.style.transition = animate && !reduceMotion.matches ? '' : 'none';
      img.style.transform = scale === 1 ? '' : `translate3d(${x}px, ${y}px, 0) scale(${scale})`;
      this.dialog.classList.toggle('is-zoomed', scale > 1);
      if (scale > 1.2 && !img.dataset.hires) {
        img.dataset.hires = '1';
        img.sizes = `${Math.round(this.stage.clientWidth * MAX_SCALE * 0.75)}px`;
      }
      this.syncZoomButton();
    }

    bounds(scale) {
      const img = this.currentImg();
      if (!img) return { x: 0, y: 0 };
      const rect = img.getBoundingClientRect();
      const baseW = rect.width / this.zoom.scale;
      const baseH = rect.height / this.zoom.scale;
      return {
        x: Math.max(0, (baseW * scale - this.stage.clientWidth) / 2),
        y: Math.max(0, (baseH * scale - this.stage.clientHeight) / 2),
      };
    }

    zoomTo(scale, clientX, clientY, animate = true) {
      if (!this.canZoom()) return;
      const next = clamp(scale, 1, MAX_SCALE);
      const stageRect = this.stage.getBoundingClientRect();
      const px = (clientX == null ? stageRect.left + stageRect.width / 2 : clientX) - (stageRect.left + stageRect.width / 2);
      const py = (clientY == null ? stageRect.top + stageRect.height / 2 : clientY) - (stageRect.top + stageRect.height / 2);
      const ratio = next / this.zoom.scale;
      let x = px - (px - this.zoom.x) * ratio;
      let y = py - (py - this.zoom.y) * ratio;
      const limit = this.bounds(next);
      x = clamp(x, -limit.x, limit.x);
      y = clamp(y, -limit.y, limit.y);
      this.zoom = next === 1 ? { scale: 1, x: 0, y: 0 } : { scale: next, x, y };
      this.applyZoom(animate);
    }

    panBy(dx, dy) {
      const limit = this.bounds(this.zoom.scale);
      this.zoom.x = clamp(this.zoom.x + dx, -limit.x, limit.x);
      this.zoom.y = clamp(this.zoom.y + dy, -limit.y, limit.y);
      this.applyZoom(false);
    }

    resetZoom(instant) {
      if (this.zoom.scale === 1) return;
      this.zoom = { scale: 1, x: 0, y: 0 };
      this.applyZoom(!instant);
    }

    syncZoomButton() {
      const zoomed = this.zoom.scale > 1;
      this.zoomBtn.disabled = !this.canZoom();
      this.zoomBtn.setAttribute('aria-pressed', zoomed ? 'true' : 'false');
      this.zoomBtn.setAttribute('aria-label', zoomed ? 'Zoom out' : 'Zoom in');
      const plus = this.zoomBtn.querySelector('[data-gv-zoom-plus]');
      if (plus) plus.setAttribute('d', zoomed ? 'm20 20-3.5-3.5M8 11h6' : 'm20 20-3.5-3.5M8 11h6M11 8v6');
    }

    /* ------------------------------------------------------------ gestures */

    onPointerDown(event) {
      if (event.target.closest('video, iframe, button')) return;
      try {
        this.stage.setPointerCapture(event.pointerId);
      } catch (error) {
        /* Capture is a nicety; gestures still track via the stage listeners. */
      }
      this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

      if (this.pointers.size === 2) {
        const [a, b] = [...this.pointers.values()];
        this.pinch = { dist: Math.hypot(a.x - b.x, a.y - b.y), scale: this.zoom.scale, cx: (a.x + b.x) / 2, cy: (a.y + b.y) / 2 };
        this.drag = null;
        return;
      }

      this.drag = { x: event.clientX, y: event.clientY, lastX: event.clientX, lastY: event.clientY, t: performance.now(), axis: null, moved: false };
    }

    onPointerMove(event) {
      if (!this.pointers.has(event.pointerId)) return;
      this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

      if (this.pinch && this.pointers.size === 2) {
        const [a, b] = [...this.pointers.values()];
        const dist = Math.hypot(a.x - b.x, a.y - b.y);
        this.zoomTo(this.pinch.scale * (dist / this.pinch.dist), this.pinch.cx, this.pinch.cy, false);
        return;
      }

      const drag = this.drag;
      if (!drag) return;
      const dx = event.clientX - drag.x;
      const dy = event.clientY - drag.y;
      if (!drag.moved && Math.hypot(dx, dy) < 6) return;
      drag.moved = true;

      if (this.zoom.scale > 1) {
        this.panBy(event.clientX - drag.lastX, event.clientY - drag.lastY);
        drag.lastX = event.clientX;
        drag.lastY = event.clientY;
        return;
      }

      if (!drag.axis) drag.axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
      if (drag.axis === 'x') {
        const width = this.stage.clientWidth;
        const atEdge = (this.index === 0 && dx > 0) || (this.index === this.order.length - 1 && dx < 0);
        const offset = atEdge ? dx * 0.3 : dx;
        this.track.style.transition = 'none';
        this.track.style.transform = `translate3d(calc(${-this.index * 100}% + ${offset}px), 0, 0)`;
        drag.offset = offset / width;
      } else if (event.pointerType !== 'mouse' && dy > 0) {
        const progress = Math.min(1, dy / 320);
        this.stage.style.transform = `translate3d(0, ${dy}px, 0) scale(${1 - progress * 0.12})`;
        this.dialog.style.setProperty('--gv-dismiss', String(progress));
      }
    }

    onPointerUp(event) {
      if (!this.pointers.has(event.pointerId)) return;
      this.pointers.delete(event.pointerId);
      if (this.pinch) {
        if (this.pointers.size < 2) this.pinch = null;
        if (this.zoom.scale < 1.05) this.resetZoom();
        this.drag = null;
        return;
      }

      const drag = this.drag;
      this.drag = null;
      if (!drag) return;

      if (!drag.moved) {
        this.onTap(event);
        return;
      }
      if (this.zoom.scale > 1) return;

      const dx = event.clientX - drag.x;
      const dy = event.clientY - drag.y;
      const elapsed = Math.max(1, performance.now() - drag.t);
      if (drag.axis === 'x') {
        const fast = Math.abs(dx) / elapsed > 0.5;
        if ((Math.abs(drag.offset || 0) > SWIPE_RATIO || fast) && dx < 0) this.go(this.index + 1);
        else if ((Math.abs(drag.offset || 0) > SWIPE_RATIO || fast) && dx > 0) this.go(this.index - 1);
        else this.go(this.index);
      } else if (drag.axis === 'y') {
        this.stage.style.transition = '';
        this.stage.style.transform = '';
        this.dialog.style.removeProperty('--gv-dismiss');
        if (dy > 120) this.close();
      }
    }

    onTap(event) {
      const now = performance.now();
      const last = this.lastTap;
      this.lastTap = { t: now, x: event.clientX, y: event.clientY };
      const isDouble = last && now - last.t < 300 && Math.hypot(event.clientX - last.x, event.clientY - last.y) < 30;
      /* Pointer capture retargets pointerup to the stage, so hit-test by position. */
      const img = this.currentImg();
      const rect = img && img.getBoundingClientRect();
      const onImage = !!rect && event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom;
      if ((event.pointerType === 'mouse' && (onImage || this.zoom.scale > 1)) || isDouble) {
        this.lastTap = null;
        if (this.zoom.scale > 1) this.resetZoom();
        else this.zoomTo(TAP_ZOOM, event.clientX, event.clientY);
      }
    }

    onWheel(event) {
      if (this.gridView.hidden === false) return;
      if (event.ctrlKey || this.zoom.scale > 1) {
        event.preventDefault();
        if (event.ctrlKey) {
          this.zoomTo(this.zoom.scale * Math.exp(-event.deltaY * 0.01), event.clientX, event.clientY, false);
        } else {
          this.panBy(-event.deltaX, -event.deltaY);
        }
        return;
      }
      /* Horizontal trackpad swipe pages through slides. */
      if (Math.abs(event.deltaX) > Math.abs(event.deltaY) && Math.abs(event.deltaX) > 24) {
        event.preventDefault();
        const now = performance.now();
        if (this.wheelLock && now - this.wheelLock < 450) return;
        this.wheelLock = now;
        this.go(this.index + (event.deltaX > 0 ? 1 : -1));
      }
    }

    /* ---------------------------------------------------------------- grid */

    toggleGrid() {
      const show = this.gridView.hidden;
      if (show) this.openedFromViewer = true;
      this.showGrid(show);
    }

    showGrid(show) {
      this.gridView.hidden = !show;
      this.dialog.classList.toggle('is-grid', show);
      this.gridBtn.setAttribute('aria-pressed', show ? 'true' : 'false');
      this.gridLabel.textContent = show ? 'Back to viewer' : `All photos (${this.order.length})`;
      if (show) {
        this.resetZoom(true);
        this.activateMedia(this.index, false);
        [...this.gridInner.children].forEach((tile, n) => tile.classList.toggle('is-current', n === this.index));
        const current = this.gridInner.children[this.index];
        if (current) this.gridView.scrollTop = Math.max(0, current.offsetTop - this.gridView.clientHeight / 3);
        if (current) current.focus({ preventScroll: true });
      } else {
        this.go(this.index, { instant: true });
      }
    }

    /* ------------------------------------------------------- open / close */

    open(key, { grid = false } = {}) {
      if (!this.built) this.build();
      const start = Math.max(0, this.order.indexOf(key));
      this.openedFromViewer = false;
      this.lastFocus = document.activeElement;
      if (typeof this.dialog.showModal === 'function') this.dialog.showModal();
      else this.dialog.setAttribute('open', '');
      document.body.classList.add('overflow-hidden');
      this.index = -1;
      this.go(start, { instant: true });
      this.showGrid(grid);
    }

    close() {
      if (typeof this.dialog.close === 'function' && this.dialog.open) this.dialog.close();
      else this.onClosed();
    }

    onClosed() {
      document.body.classList.remove('overflow-hidden');
      this.activateMedia(this.index, false);
      this.resetZoom(true);
      this.stage.style.transform = '';
      this.dialog.style.removeProperty('--gv-dismiss');
      const key = this.order && this.order[this.index];
      if (key && this.gallery && typeof this.gallery.setActiveMedia === 'function') {
        try {
          this.gallery.setActiveMedia(key, false);
        } catch (error) {
          /* setActiveMedia is best-effort; the page gallery stays where it was. */
        }
      }
      if (this.lastFocus && this.lastFocus.isConnected) this.lastFocus.focus({ preventScroll: true });
    }

    bind() {
      const d = this.dialog;
      d.querySelector('[data-gv-close]').addEventListener('click', () => this.close());
      d.querySelector('[data-gv-prev]').addEventListener('click', () => this.go(this.index - 1));
      d.querySelector('[data-gv-next]').addEventListener('click', () => this.go(this.index + 1));
      this.gridBtn.addEventListener('click', () => this.toggleGrid());
      this.zoomBtn.addEventListener('click', () => (this.zoom.scale > 1 ? this.resetZoom() : this.zoomTo(TAP_ZOOM)));

      this.thumbs.addEventListener('click', (event) => {
        const thumb = event.target.closest('.egj-gv__thumb');
        if (thumb) this.go(Number(thumb.dataset.index));
      });
      this.gridInner.addEventListener('click', (event) => {
        const tile = event.target.closest('.egj-gv__tile');
        if (!tile) return;
        this.index = Number(tile.dataset.index);
        this.showGrid(false);
      });

      this.stage.addEventListener('pointerdown', (event) => this.onPointerDown(event));
      this.stage.addEventListener('pointermove', (event) => this.onPointerMove(event));
      this.stage.addEventListener('pointerup', (event) => this.onPointerUp(event));
      this.stage.addEventListener('pointercancel', (event) => this.onPointerUp(event));
      this.stage.addEventListener('wheel', (event) => this.onWheel(event), { passive: false });
      this.stage.addEventListener('dblclick', (event) => event.preventDefault());

      d.addEventListener('keydown', (event) => {
        if (event.target.closest('input, textarea, video')) return;
        const grid = !this.gridView.hidden;
        switch (event.key) {
          case 'ArrowRight':
            if (grid) return;
            event.preventDefault();
            this.go(this.index + 1);
            break;
          case 'ArrowLeft':
            if (grid) return;
            event.preventDefault();
            this.go(this.index - 1);
            break;
          case 'Home':
            if (grid) return;
            event.preventDefault();
            this.go(0);
            break;
          case 'End':
            if (grid) return;
            event.preventDefault();
            this.go(this.order.length - 1);
            break;
          case '+':
          case '=':
            event.preventDefault();
            this.zoomTo(this.zoom.scale * 1.5);
            break;
          case '-':
            event.preventDefault();
            this.zoomTo(this.zoom.scale / 1.5);
            break;
          case '0':
            this.resetZoom();
            break;
          case 'g':
          case 'G':
            this.toggleGrid();
            break;
        }
      });

      /* Esc steps back out of a zoom, or from a grid opened inside the viewer,
         before it closes the dialog. */
      d.addEventListener('cancel', (event) => {
        if (!this.gridView.hidden && this.openedFromViewer) {
          event.preventDefault();
          this.showGrid(false);
        } else if (this.zoom.scale > 1) {
          event.preventDefault();
          this.resetZoom();
        }
      });

      d.addEventListener('close', () => this.onClosed());
      d.addEventListener('click', (event) => {
        if (event.target === d) this.close();
      });
      window.addEventListener('resize', () => {
        if (!d.open) return;
        this.resetZoom(true);
        this.go(this.index, { instant: true });
      });
    }
  }

  const viewers = new Map();
  const viewerFor = (sectionId) => {
    const dialog = document.querySelector(`dialog[data-egj-gv="${sectionId}"]`);
    if (!dialog) return null;
    if (!viewers.has(dialog)) viewers.set(dialog, new Viewer(dialog));
    return viewers.get(dialog);
  };

  /* Capture phase, so the legacy <modal-opener> never opens its modal. */
  document.addEventListener(
    'click',
    (event) => {
      const more = event.target.closest('.egj-pdp-thumbs__more');
      const toggle = !more && event.target.closest('media-gallery .product__media-toggle');
      if (!more && !toggle) return;
      const gallery = event.target.closest('media-gallery');
      const sectionId = gallery && gallery.id.replace('MediaGallery-', '');
      const viewer = sectionId && viewerFor(sectionId);
      if (!viewer) return;

      const slide = event.target.closest('.product__media-item[data-media-id]') || gallery.querySelector('.product__media-item.is-active');
      const key = slide && slide.dataset.mediaId;
      const item = key && viewer.data[key];
      if (toggle && item && item.type === 'model') return;

      event.preventDefault();
      event.stopImmediatePropagation();
      viewer.open(key, { grid: !!more });
    },
    true
  );

  document.addEventListener('shopify:section:load', () => viewers.clear());
})();
